'use client';

import { Button, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgChevronright } from '@common/icon';
import { zodResolver } from '@common/utils';
import { z } from '@core/utils';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { VERIFICATION_TYPE } from '@/api';
import { ButtonFooter } from '@/components/server';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

import { Checkbox } from './Checkbox';
import { JejuTerm } from './JejuTerm';

type ConsentForm = {
  isProvideInfoConsented: boolean;
  isPrivacyAgreement: boolean;
  isYouthProgramParticipated: boolean;
  isSensitiveInfoCollected: boolean;
};

type TermCategory =
  | 'provide_agreement'
  | 'privacy_agreement'
  | 'consulting_agreement'
  | 'collection_agreement';

const consentKeys: Record<TermCategory, keyof ConsentForm> = {
  provide_agreement: 'isProvideInfoConsented',
  privacy_agreement: 'isPrivacyAgreement',
  consulting_agreement: 'isYouthProgramParticipated',
  collection_agreement: 'isSensitiveInfoCollected',
};

const schema = z.object({
  isProvideInfoConsented: z
    .boolean()
    .refine((val) => val === true, '개인정보 제3자 제공 동의가 필요합니다.'),
  isPrivacyAgreement: z
    .boolean()
    .refine((val) => val === true, '개인정보처리방침 동의가 필요합니다.'),
  isYouthProgramParticipated: z
    .boolean()
    .refine(
      (val) => val === true,
      '청년이어드림 프로그램 참여 동의가 필요합니다.',
    ),
  isSensitiveInfoCollected: z
    .boolean()
    .refine(
      (val) => val === true,
      '상담녹화 및 민감정보 수집·이용에 대한 동의가 필요합니다.',
    ),
});

export const TermsAgreementCheckList = () => {
  const [isLogin, myInfo] = useAuthStore(
    useShallow((state) => [state.isLogin, state.myInfo] as const),
  );
  const { toast } = useToast();
  const router = useCustomRouter();
  const { setValue, handleSubmit, watch } = useForm<ConsentForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      isProvideInfoConsented: false,
      isPrivacyAgreement: false,
      isYouthProgramParticipated: false,
      isSensitiveInfoCollected: false,
    },
  });
  const [isTermOpened, setIsTermOpened] = useState(false);
  const [activeTerm, setActiveTerm] =
    useState<TermCategory>('privacy_agreement');
  const [isModalOpened, setIsModalOpened] = useState(false);

  const handleTermClick = (term: TermCategory) => {
    const consentKey = consentKeys[term];

    if (!watch(consentKey)) {
      setIsTermOpened(true);
      setActiveTerm(term);
    } else {
      setValue(consentKey, false);
    }
  };

  const handleConfirmationClick = (term: TermCategory) => {
    if (term === 'provide_agreement') {
      setValue('isProvideInfoConsented', true);
    }

    if (term === 'privacy_agreement') {
      setValue('isPrivacyAgreement', true);
    }

    if (term === 'consulting_agreement') {
      setValue('isYouthProgramParticipated', true);
    }

    if (term === 'collection_agreement') {
      setValue('isSensitiveInfoCollected', true);
    }
  };

  const allAgreed =
    watch('isProvideInfoConsented') &&
    watch('isPrivacyAgreement') &&
    watch('isYouthProgramParticipated') &&
    watch('isSensitiveInfoCollected');

  return (
    <>
      <form
        className="text-[0.875rem]"
        onSubmit={handleSubmit(
          () => {
            if (isLogin) {
              if (myInfo?.isVerification) {
                return router.push(
                  ROUTES.EASY_CERTIFICATION.withSearchParams({
                    searchParams: {
                      verification: VERIFICATION_TYPE.POLICY_APPLY,
                      applyPolicyId: 2,
                    },
                  }),
                );
              } else {
                setIsModalOpened(true);
              }
            } else {
              router.push(
                ROUTES.LOGIN.withSearchParams({
                  searchParams: {
                    redirect: ROUTES.EASY_CERTIFICATION.withSearchParams({
                      searchParams: {
                        verification: VERIFICATION_TYPE.POLICY_APPLY,
                        applyPolicyId: 2,
                      },
                    }),
                  },
                }),
              );
            }
          },
          (error) => {
            for (const key in error) {
              const message = error[key as keyof typeof error]?.message;

              if (message) {
                return toast({
                  message,
                  type: 'fail',
                });
              }
            }
          },
        )}
      >
        <header className="font-bold pb-16 text-[1.25rem] text-Gray-900">
          정책을 바로 신청하기 위한
          <br />
          약관에 동의해 주세요.
        </header>
        <fieldset className="flex flex-col border-none [&>*]:w-full">
          <Checkbox
            checked={allAgreed}
            className="font-bold py-10"
            onChange={(e) => {
              const { checked } = e.target;
              setValue('isProvideInfoConsented', checked);
              setValue('isPrivacyAgreement', checked);
              setValue('isYouthProgramParticipated', checked);
              setValue('isSensitiveInfoCollected', checked);
            }}
          >
            전체동의
          </Checkbox>
          <div
            className="flex cursor-pointer justify-between py-10 [&>label]:cursor-pointer"
            onClick={() => handleTermClick('provide_agreement')}
          >
            <Checkbox
              checkable={false}
              checked={watch('isProvideInfoConsented')}
              onClick={(e) => e.stopPropagation()}
            >
              (필수) 개인정보 제3자 제공 동의(제주청년센터)
            </Checkbox>
            <SvgChevronright className="size-20 text-Gray-600" />
          </div>
          <div
            className="flex cursor-pointer justify-between py-10 [&>label]:cursor-pointer"
            onClick={() => handleTermClick('privacy_agreement')}
          >
            <Checkbox
              checkable={false}
              checked={watch('isPrivacyAgreement')}
              onClick={(e) => e.stopPropagation()}
            >
              (필수) 개인정보처리방침 동의
            </Checkbox>
            <SvgChevronright className="size-20 text-Gray-600" />
          </div>
          <div
            className="flex cursor-pointer justify-between py-10 [&>label]:cursor-pointer"
            onClick={() => handleTermClick('consulting_agreement')}
          >
            <Checkbox
              checkable={false}
              checked={watch('isYouthProgramParticipated')}
              onClick={(e) => e.stopPropagation()}
            >
              (필수) 청년이어드림 프로그램 참여
            </Checkbox>
            <SvgChevronright className="size-20 text-Gray-600" />
          </div>
          <div
            className="flex cursor-pointer justify-between py-10 [&>label]:cursor-pointer"
            onClick={() => handleTermClick('collection_agreement')}
          >
            <Checkbox
              checkable={false}
              checked={watch('isSensitiveInfoCollected')}
              onClick={(e) => e.stopPropagation()}
            >
              (필수) 상담녹화 및 민감정보 수집·이용에 대한 동의
            </Checkbox>
            <SvgChevronright className="size-20 text-Gray-600" />
          </div>
        </fieldset>
        <ButtonFooter
          className="border-t-0 p-0"
          disabled={!allAgreed}
          type="submit"
        >
          신청하기
        </ButtonFooter>
        <JejuTerm
          opened={isTermOpened}
          termType={activeTerm}
          onClose={() => setIsTermOpened(false)}
          onConfirmationClick={() => handleConfirmationClick(activeTerm)}
        />
      </form>
      <Modal opened={isModalOpened} onClose={() => setIsModalOpened(false)}>
        <h2 className="font-bold m-0 pb-10 text-[1.25rem] text-Gray-900">본인인증이 필요해요.</h2>
        먼저 내 정보에서 본인인증을 진행해주세요.
        <br />
        완료한 후 다시 시도해주세요.
        <footer className="mt-30 flex gap-8">
          <Button
            className="flex-1"
            theme="primary-line"
            onClick={() => setIsModalOpened(false)}
          >
            취소
          </Button>
          <Button
            className="flex-1"
            onClick={() => router.push(ROUTES.MY_EDIT.pathname)}
          >
            내 정보 가기
          </Button>
        </footer>
      </Modal>
    </>
  );
};
